import type {
  Paginated,
  ProductDetails,
  ProductImportResult,
  ProductPricingMode,
  ProductSummary,
  RecordStatus,
} from '@ldembalagens/types';
import { api, apiFetch, downloadFile, type QueryParams } from '@/lib/api-client';
import { multipart } from '@/features/invoices/invoices-api';

export interface ProductQuery extends QueryParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: RecordStatus;
  categoryId?: string;
  isCustom?: boolean;
  showOnSite?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface ProductInput {
  code?: string;
  sku?: string | null;
  name: string;
  description?: string | null;
  categoryId?: string | null;
  unitId: string;
  lengthCm?: string | null;
  widthCm?: string | null;
  heightCm?: string | null;
  weightKg?: string | null;
  woodType?: string | null;
  treatmentType?: string | null;
  loadCapacityKg?: string | null;
  estimatedCost?: string;
  salePrice?: string;
  pricingMode?: ProductPricingMode;
  pricePerM3?: string | null;
  allowCustomDimensions?: boolean;
  pieceLengthMm?: string | null;
  pieceWidthMm?: string | null;
  pieceThicknessMm?: string | null;
  minStock?: string;
  isCustom?: boolean;
  imageUrl?: string | null;
  showOnSite?: boolean;
  siteDescription?: string | null;
  siteSortOrder?: number;
  notes?: string | null;
  status?: RecordStatus;
  specifications?: {
    id?: string;
    name: string;
    value: string;
    unit?: string | null;
    sortOrder?: number;
  }[];
}

export interface ProductVariantInput {
  pieceLengthMm: string;
  pieceWidthMm: string;
  pieceThicknessMm: string;
  pricePerM3?: string | null;
  code?: string | null;
  name?: string | null;
}

export const productsApi = {
  createVariant: (id: string, input: ProductVariantInput) =>
    api.post<ProductDetails>(`/products/${id}/variants`, input),
  list: (query: ProductQuery) => api.get<Paginated<ProductSummary>>('/products', query),
  options: () =>
    api.get<{ id: string; code: string; name: string; unitCode: string }[]>('/products/options'),
  get: (id: string) => api.get<ProductDetails>(`/products/${id}`),
  create: (input: ProductInput) => api.post<ProductDetails>('/products', input),
  update: (id: string, input: Partial<ProductInput>) =>
    api.patch<ProductDetails>(`/products/${id}`, input),
  duplicate: (id: string, input: { name?: string } = {}) =>
    api.post<ProductDetails>(`/products/${id}/duplicate`, input),
  remove: (id: string) => api.delete(`/products/${id}`),
  importTemplate: () => downloadFile('/products/import/template'),
  /** Importação por CSV: `dryRun` só valida e devolve a prévia linha a linha. */
  importCsv: (file: File, opts: { dryRun: boolean; updateExisting: boolean }) =>
    apiFetch<ProductImportResult>('/products/import', {
      method: 'POST',
      body: multipart(file, {
        dryRun: String(opts.dryRun),
        updateExisting: String(opts.updateExisting),
      }),
    }),
};

export const productsKeys = {
  all: ['products'] as const,
  list: (q: ProductQuery) => ['products', 'list', q] as const,
  detail: (id: string) => ['products', 'detail', id] as const,
  options: ['products', 'options'] as const,
};
